import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import catalogService from '../../utils/catalogService';

const MultiSelectField = ({ field, value, onChange, error, readonly }) => {
  const { id, name, label, required, catalogReference, hint } = field;
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  
  // Cargar las opciones del catálogo de forma asíncrona
  useEffect(() => {
    const loadOptions = async () => {
      if (!catalogReference) {
        setOptions([]);
        return;
      }
      
      setIsLoading(true);
      try {
        const catalogOptions = await catalogService.getOptions(catalogReference);
        setOptions(catalogOptions);
      } catch (error) {
        // console.error('Error al cargar opciones del catálogo:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadOptions();
  }, [catalogReference]);
  
  // Asegurar que value sea un array
  // Manejar tanto arrays como strings separados por comas o espacios
  const selectedValues = useMemo(() => {
    return Array.isArray(value) 
      ? value 
      : (value && typeof value === 'string') 
        ? (value.includes(',') 
            ? value.split(',').map(v => v.trim()).filter(v => v !== '') 
            : value.includes(' ') 
              ? value.split(' ').filter(v => v.trim() !== '') 
              : [value])
        : (value ? [value] : []);
  }, [value]);
  
  const handleChange = (selectedOptions) => {
    const newValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    
    // Mantener el mismo formato que se recibió
    // Si el valor original era un string separado por comas o espacios, devolver string
    // Si era un array, devolver array
    const shouldReturnString = value && typeof value === 'string' && (value.includes(',') || value.includes(' '));
    const separator = value && typeof value === 'string' && value.includes(',') ? ',' : ' ';
    const result = newValues.length > 0 
      ? (shouldReturnString ? newValues.join(separator) : newValues)
      : null;
    
    onChange(name, result);
  };

  const fieldId = `multiselect_${id || name}`;
  const hasError = error && error.length > 0;

  // Convertir opciones al formato de react-select
  const selectOptions = useMemo(() => {
    return options.map(option => ({
      value: option.originalId,
      label: option.label
    }));
  }, [options]);

  // Efecto para actualizar las opciones seleccionadas cuando cambien las opciones o los valores
  useEffect(() => {
    if (selectOptions.length === 0) {
      setSelectedOptions([]);
      return;
    }
    
    // Comparación flexible que maneja diferentes tipos de datos
    const foundOptions = selectOptions.filter(option => {
      return selectedValues.some(selectedValue => {
        // Comparación estricta primero con value
        if (option.value === selectedValue) return true;
        // Comparación flexible para strings y números con value
        if (String(option.value) === String(selectedValue)) return true;
        // Comparación numérica si ambos pueden ser números con value
        if (!isNaN(option.value) && !isNaN(selectedValue) && Number(option.value) === Number(selectedValue)) return true;
        
        // Si no coincide con value, intentar con originalId (para catálogos)
        const originalOption = options.find(opt => opt.value === option.value);
        if (originalOption && originalOption.originalId) {
          if (originalOption.originalId === selectedValue) return true;
          if (String(originalOption.originalId) === String(selectedValue)) return true;
          if (!isNaN(originalOption.originalId) && !isNaN(selectedValue) && Number(originalOption.originalId) === Number(selectedValue)) return true;
        }
        
        return false;
      });
    });
    
    setSelectedOptions(foundOptions);
  }, [selectOptions, selectedValues, options]);

  // Estilos personalizados para react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: hasError ? '#dc3545' : state.isFocused ? '#007bff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      '&:hover': {
        borderColor: hasError ? '#dc3545' : '#007bff'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e9ecef',
      borderRadius: '0.25rem'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#495057'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#6c757d',
      '&:hover': {
        backgroundColor: '#dc3545',
        color: 'white'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  return (
    <div className="form-field multiselect-field">
      <Select
        id={fieldId}
        name={name}
        value={selectedOptions}
        onChange={handleChange}
        options={selectOptions}
        placeholder={`Seleccionar`}
        isMulti
        isClearable
        isSearchable
        isLoading={isLoading}
        isDisabled={isLoading || readonly}
        styles={customStyles}
        className={hasError ? 'react-select-error' : ''}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        noOptionsMessage={() => 'No hay opciones disponibles'}
        loadingMessage={() => 'Cargando opciones...'}
      />
      
      {!isLoading && selectedValues.length > 0 && (
        <div className="selected-summary">
          Seleccionados: {selectedValues.length} de {options.length}
        </div>
      )}
      
      
      
      {catalogReference && !isLoading && options.length === 0 && (
        <div className="field-warning">
          ⚠️ Catálogo '{catalogReference}' no encontrado o vacío
        </div>
      )}
      
      <style>{`
        .form-field {
          margin-bottom: 20px;
        }
        
        .field-label {
          display: block;
          font-weight: 600;
          margin-bottom: 12px;
          color: #333;
          font-size: 14px;
        }
        
        .field-label.required {
          color: #1976d2;
        }
        
        .required-indicator {
          color: #d32f2f;
        }
        
        .field-hint {
          font-size: 12px;
          color: #666;
          margin-bottom: 12px;
          font-style: italic;
        }
        
        .checkbox-group {
          border: 2px solid #e0e0e0;
          border-radius: 6px;
          padding: 12px;
          background-color: white;
          max-height: 300px;
          overflow-y: auto;
        }
        
        .checkbox-group.error {
          border-color: #d32f2f;
        }
        
        .checkbox-item {
          margin-bottom: 8px;
          display: flex;
          align-items: flex-start;
        }
        
        .checkbox-item:last-child {
          margin-bottom: 0;
        }
        
        .checkbox-input {
          display: none;
        }
        
        .checkbox-label {
          display: flex;
          align-items: flex-start;
          cursor: pointer;
          font-size: 14px;
          line-height: 1.4;
          width: 100%;
        }
        
        .checkbox-custom {
          width: 18px;
          height: 18px;
          border: 2px solid #ccc;
          border-radius: 3px;
          margin-right: 10px;
          margin-top: 1px;
          flex-shrink: 0;
          position: relative;
          transition: all 0.2s ease;
        }
        
        .checkbox-input:checked + .checkbox-label .checkbox-custom {
          background-color: #1976d2;
          border-color: #1976d2;
        }
        
        .checkbox-input:checked + .checkbox-label .checkbox-custom::after {
          content: '✓';
          position: absolute;
          top: -2px;
          left: 2px;
          color: white;
          font-size: 12px;
          font-weight: bold;
        }
        
        .checkbox-label:hover .checkbox-custom {
          border-color: #1976d2;
        }
        
        .checkbox-text {
          flex: 1;
          word-break: break-word;
        }
        
        .selected-summary {
          margin-top: 8px;
          font-size: 12px;
          color: #1976d2;
          font-weight: 500;
          padding: 4px 8px;
          background-color: #e3f2fd;
          border-radius: 4px;
          display: inline-block;
        }
        
        .field-error {
          color: #d32f2f;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
        }
        
        .field-error::before {
          content: '⚠️';
          margin-right: 4px;
        }
        
        .field-warning {
          color: #f57c00;
          font-size: 12px;
          margin-top: 4px;
          padding: 8px;
          background-color: #fff3e0;
          border-radius: 4px;
          border-left: 4px solid #f57c00;
        }
        
        .multiselect-field {
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default MultiSelectField;